// Pure-data timelines for the onboarding tour's HTML-animated demo scenes.
// No React/DOM imports here on purpose: TourDemoScene.tsx (and the per-scene
// stage components under components/tourDemoStages/) own the rendering and
// timer scheduling; this module only describes WHAT happens and WHEN, so it
// can be unit-tested without mounting anything (see tourScenes.test.ts).
//
// Coordinates are on a fixed 1100x688 internal canvas (CANVAS_W/H below),
// matching the geometry the stage components actually render at that size —
// see each stage file for the layout math the coordinates below assume.

import type { ReviewKind } from '../../shared/progress';

export type SceneId = 'suggest-edits' | 'comments' | 'general-notes';

export const CANVAS_W = 1100;
export const CANVAS_H = 688;

export type SceneVarValue = string | number | boolean;
export type SceneVars = Record<string, SceneVarValue>;

export type SceneAction =
  | { kind: 'cursor'; x: number; y: number; ms: number }
  | { kind: 'click' }
  | { kind: 'cursorHide' }
  | { kind: 'beat'; text: string | null }
  | { kind: 'overlay'; which: 'enter' | 'exit'; show: boolean }
  | { kind: 'veil'; show: boolean }
  | { kind: 'set'; key: string; value: SceneVarValue }
  | {
      kind: 'type';
      key: string;
      text: string;
      charMs: number;
      // Side effects fired once the typed substring reaches a given length —
      // e.g. flipping a "Saving" chip on partway through a run of typing.
      milestones?: { charCount: number; sets: { key: string; value: SceneVarValue }[] }[];
    };

export interface SceneStep {
  at: number;
  action: SceneAction;
}

export interface SceneDefinition {
  id: SceneId;
  /** Accessible label for the outer frame, e.g. "Comments demo". Also used
   *  to build the Play/Replay button aria-labels. */
  label: string;
  initialCursor: { x: number; y: number };
  initialVars: SceneVars;
  timeline: SceneStep[];
}

const TYPED_SUGGESTION = ' The city wakes up with us.';
const TYPED_COMMENT_NAME = 'Dana';
const TYPED_COMMENT_BODY = 'Love this. Could we name a few of the cafes we supply?';
const TYPED_NOTE = 'Tone feels right for us. Could the ending focus more on the cafe community?';

// ---------------------------------------------------------------------------
// suggest-edits — ported from mockups/tour-refinement-2.html's choreography.
// Page A (review, before) -> Page B (suggest mode) -> Page C (review, after).
// vars: page ('A'|'B'|'C'), suggestActionLit, bannerLit, editingCell,
// resumeActionLit, noticeShown, saveState ('idle'|'saving'|'saved'), typed.
// ---------------------------------------------------------------------------
const suggestEdits: SceneDefinition = {
  id: 'suggest-edits',
  label: 'Suggest edits demo',
  initialCursor: { x: 1060, y: 660 },
  initialVars: {
    page: 'A',
    suggestActionLit: false,
    bannerLit: false,
    editingCell: false,
    resumeActionLit: false,
    noticeShown: false,
    saveState: 'idle',
    typed: '',
  },
  timeline: [
    { at: 300, action: { kind: 'beat', text: '1 · Open suggest edits' } },
    { at: 500, action: { kind: 'cursor', x: 550, y: 248, ms: 1200 } },
    { at: 1600, action: { kind: 'set', key: 'suggestActionLit', value: true } },
    { at: 2000, action: { kind: 'click' } },
    { at: 2500, action: { kind: 'overlay', which: 'enter', show: true } },
    { at: 2500, action: { kind: 'beat', text: null } },
    { at: 2950, action: { kind: 'set', key: 'page', value: 'B' } },
    { at: 2950, action: { kind: 'cursor', x: 700, y: 420, ms: 0 } },
    { at: 3900, action: { kind: 'overlay', which: 'enter', show: false } },
    { at: 3900, action: { kind: 'beat', text: '2 · The script becomes editable' } },
    { at: 4300, action: { kind: 'cursor', x: 428, y: 158, ms: 900 } },
    { at: 5300, action: { kind: 'click' } },
    { at: 5300, action: { kind: 'set', key: 'editingCell', value: true } },
    { at: 5900, action: { kind: 'beat', text: '3 · Type your change, it autosaves' } },
    {
      at: 5900,
      action: {
        kind: 'type',
        key: 'typed',
        text: TYPED_SUGGESTION,
        charMs: 52,
        // "saved" is anchored to the typing action's own last-char milestone
        // rather than a separate absolute-time step: a chained per-char
        // interval can drift later than a flat setTimeout under heavy timer
        // throttling (e.g. a backgrounded tab), and a flat "saved" step
        // scheduled to land after the *nominal* typing duration could then
        // fire before the typing chain reaches its own "saving" milestone,
        // visibly reverting Saved back to Saving with nothing left to
        // correct it. Tying both to the same chain keeps them in order no
        // matter how the browser paces the timers.
        milestones: [
          { charCount: 6, sets: [{ key: 'saveState', value: 'saving' }] },
          { charCount: TYPED_SUGGESTION.length, sets: [{ key: 'saveState', value: 'saved' }] },
        ],
      },
    },
    { at: 8700, action: { kind: 'beat', text: '4 · Done editing hands it back' } },
    { at: 8700, action: { kind: 'cursor', x: 1011, y: 28, ms: 1200 } },
    { at: 9800, action: { kind: 'set', key: 'bannerLit', value: true } },
    { at: 10200, action: { kind: 'click' } },
    { at: 10700, action: { kind: 'overlay', which: 'exit', show: true } },
    { at: 11050, action: { kind: 'set', key: 'page', value: 'C' } },
    { at: 11050, action: { kind: 'cursor', x: 700, y: 500, ms: 0 } },
    { at: 11400, action: { kind: 'overlay', which: 'exit', show: false } },
    { at: 11400, action: { kind: 'beat', text: '5 · Your edit is saved for the team' } },
    { at: 11800, action: { kind: 'set', key: 'resumeActionLit', value: true } },
    { at: 11800, action: { kind: 'set', key: 'noticeShown', value: true } },
    { at: 12100, action: { kind: 'cursorHide' } },
    { at: 14300, action: { kind: 'beat', text: null } },
    { at: 14300, action: { kind: 'veil', show: true } },
  ],
};

// ---------------------------------------------------------------------------
// comments — a doc column (left) and a comments sidebar (right), no page
// swap: the whole beat plays out on one screen.
// vars: sweepOn, pillVisible, composerOpen, composerName, composerText,
// posted, openCount (0 or 1).
// ---------------------------------------------------------------------------
const comments: SceneDefinition = {
  id: 'comments',
  label: 'Comments demo',
  initialCursor: { x: 1060, y: 660 },
  initialVars: {
    sweepOn: false,
    pillVisible: false,
    composerOpen: false,
    composerName: '',
    composerText: '',
    posted: false,
    openCount: 0,
  },
  timeline: [
    // Coordinates below were measured against the rendered stage via a
    // headless-Chrome measurement of the dev harness (sentence "Aurora
    // roasts in small batches every week." renders one line spanning canvas
    // x 61-364, y 171-190; pill center 376,136; composer fields 906,157 /
    // 906,213; Post button 992,267) — don't re-derive them from layout math.
    // The sweep cursor rides ON the text line (dot center ≈ y 180) and its
    // end lands on the selection's right edge, matching CommentsStage's
    // text-selection sweep (same 700ms + easing).
    // Known stale, on purpose: the three composer numbers were measured when
    // the quoted sentence wrapped to two lines; since it was shortened
    // (04461dc) the fields sit one 18px line higher (906,139 / 906,192 /
    // 992,249). Left as is so this timeline stays unchanged; see
    // docs/gates/GATE-BRIEF-TOUR-SCENES.md section 7.
    { at: 300, action: { kind: 'beat', text: '1 · Highlight any line' } },
    { at: 600, action: { kind: 'cursor', x: 55, y: 172, ms: 800 } },
    { at: 1500, action: { kind: 'set', key: 'sweepOn', value: true } },
    { at: 1500, action: { kind: 'cursor', x: 356, y: 172, ms: 700 } },
    { at: 2500, action: { kind: 'set', key: 'pillVisible', value: true } },
    { at: 2500, action: { kind: 'cursor', x: 376, y: 136, ms: 500 } },
    { at: 3100, action: { kind: 'click' } },
    { at: 3400, action: { kind: 'beat', text: '2 · Leave your note right there' } },
    { at: 3400, action: { kind: 'set', key: 'composerOpen', value: true } },
    { at: 3400, action: { kind: 'cursor', x: 906, y: 157, ms: 800 } },
    { at: 4300, action: { kind: 'click' } },
    { at: 4500, action: { kind: 'type', key: 'composerName', text: TYPED_COMMENT_NAME, charMs: 90 } },
    { at: 5100, action: { kind: 'cursor', x: 906, y: 213, ms: 600 } },
    { at: 5800, action: { kind: 'click' } },
    { at: 6100, action: { kind: 'type', key: 'composerText', text: TYPED_COMMENT_BODY, charMs: 32 } },
    { at: 8100, action: { kind: 'cursor', x: 992, y: 267, ms: 700 } },
    { at: 8900, action: { kind: 'click' } },
    { at: 9200, action: { kind: 'set', key: 'composerOpen', value: false } },
    { at: 9200, action: { kind: 'set', key: 'posted', value: true } },
    { at: 9200, action: { kind: 'set', key: 'openCount', value: 1 } },
    { at: 9200, action: { kind: 'beat', text: '3 · It lands in the sidebar' } },
    { at: 11200, action: { kind: 'cursorHide' } },
    { at: 13000, action: { kind: 'beat', text: null } },
    { at: 13000, action: { kind: 'veil', show: true } },
  ],
};

// ---------------------------------------------------------------------------
// general-notes — review card page (A) soft-fades to a notes panel (B).
// vars: page ('A'|'B'), notesActionLit, saveState ('idle'|'saving'|'saved'),
// typed.
// ---------------------------------------------------------------------------
const generalNotes: SceneDefinition = {
  id: 'general-notes',
  label: 'General notes demo',
  initialCursor: { x: 1060, y: 660 },
  initialVars: {
    page: 'A',
    notesActionLit: false,
    saveState: 'idle',
    typed: '',
  },
  timeline: [
    { at: 300, action: { kind: 'beat', text: '1 · Open general notes' } },
    { at: 500, action: { kind: 'cursor', x: 876, y: 253, ms: 1100 } },
    { at: 1700, action: { kind: 'set', key: 'notesActionLit', value: true } },
    { at: 2100, action: { kind: 'click' } },
    { at: 2600, action: { kind: 'set', key: 'page', value: 'B' } },
    { at: 2600, action: { kind: 'cursor', x: 700, y: 300, ms: 0 } },
    { at: 2900, action: { kind: 'beat', text: '2 · Big-picture thoughts go here' } },
    { at: 3300, action: { kind: 'cursor', x: 550, y: 374, ms: 600 } },
    { at: 3950, action: { kind: 'click' } },
    {
      at: 4200,
      action: {
        kind: 'type',
        key: 'typed',
        text: TYPED_NOTE,
        charMs: 38,
        // "saved" rides the typing action's own final-char milestone rather
        // than a separate absolute-time step — see the matching comment on
        // the suggest-edits scene for why (chained per-char timers can drift
        // later than a flat setTimeout under heavy throttling, and a flat
        // "saved" step landing first would otherwise get silently reverted
        // back to "saving" by the chain's own milestone with nothing left to
        // correct it).
        milestones: [
          { charCount: 10, sets: [{ key: 'saveState', value: 'saving' }] },
          { charCount: TYPED_NOTE.length, sets: [{ key: 'saveState', value: 'saved' }] },
        ],
      },
    },
    { at: 7600, action: { kind: 'beat', text: '3 · It autosaves' } },
    { at: 9600, action: { kind: 'cursorHide' } },
    { at: 11000, action: { kind: 'beat', text: null } },
    { at: 11000, action: { kind: 'veil', show: true } },
  ],
};

// ---------------------------------------------------------------------------
// The brief variants. Same choreography, same beats and roughly the same
// running time as their script twins above; what changes is the document
// underneath (titled serif sections instead of AUDIO / VISUAL rows, see
// components/tourDemoStages/PageChrome.tsx) and therefore the coordinates.
// Every coordinate below was MEASURED against the rendered brief stage
// through the dev harness (preview-scenes.html, src/dev/previewScenes.tsx,
// vite dev only), never derived from layout math; the measured boxes are
// recorded beside each timeline. `data-tour-target` attributes on the brief
// stages mark the elements the harness measures.
// ---------------------------------------------------------------------------

export const BRIEF_TYPED_SUGGESTION = ' Most would rather pay per visit.';
export const BRIEF_TYPED_COMMENT_NAME = 'Priya';
export const BRIEF_TYPED_COMMENT_BODY = 'Could we say the first visit ends with a written plan?';
export const BRIEF_TYPED_NOTE = 'Tone feels right. Could we add a question about what Dana would tell a friend?';

const briefSuggestEdits: SceneDefinition = {
  id: 'suggest-edits',
  label: 'Suggest edits demo',
  initialCursor: { x: 1060, y: 660 },
  initialVars: {
    page: 'A',
    suggestActionLit: false,
    bannerLit: false,
    editingCell: false,
    resumeActionLit: false,
    noticeShown: false,
    saveState: 'idle',
    typed: '',
  },
  timeline: [
    // Measured against the rendered brief stage (canvas boxes): review card
    // action 2 x 387-713, y 221.5-285 and action 3 x 713-1039, y 221.5-285,
    // both identical to the script stage's because ReviewPageChrome only
    // swaps its hero copy; "Done editing" x 958.6-1064, y 12.5-42.5; the
    // editable Audience paragraph x 41-1059, y 257.5-320.5, whose text ends
    // at x 435.3 on the line spanning y 290-309. So the card and Done-editing
    // clicks keep the script's numbers and only the click into the paragraph
    // moves, landing on the end of its text.
    { at: 300, action: { kind: 'beat', text: '1 · Open suggest edits' } },
    { at: 500, action: { kind: 'cursor', x: 550, y: 248, ms: 1200 } },
    { at: 1600, action: { kind: 'set', key: 'suggestActionLit', value: true } },
    { at: 2000, action: { kind: 'click' } },
    { at: 2500, action: { kind: 'overlay', which: 'enter', show: true } },
    { at: 2500, action: { kind: 'beat', text: null } },
    { at: 2950, action: { kind: 'set', key: 'page', value: 'B' } },
    { at: 2950, action: { kind: 'cursor', x: 700, y: 420, ms: 0 } },
    { at: 3900, action: { kind: 'overlay', which: 'enter', show: false } },
    { at: 3900, action: { kind: 'beat', text: '2 · The brief becomes editable' } },
    { at: 4300, action: { kind: 'cursor', x: 435, y: 300, ms: 900 } },
    { at: 5300, action: { kind: 'click' } },
    { at: 5300, action: { kind: 'set', key: 'editingCell', value: true } },
    { at: 5900, action: { kind: 'beat', text: '3 · Type your change, it autosaves' } },
    {
      at: 5900,
      action: {
        kind: 'type',
        key: 'typed',
        text: BRIEF_TYPED_SUGGESTION,
        // Same pace as the script's; the addition is short enough to finish
        // well before the "Done editing" beat at 8700 (33 chars, ends 7616 nominal;
        // the chained per-char timers land about 150ms later in practice).
        charMs: 52,
        // Same milestone pattern as the script variant, for the same
        // throttling reason (see the comment there).
        milestones: [
          { charCount: 6, sets: [{ key: 'saveState', value: 'saving' }] },
          { charCount: BRIEF_TYPED_SUGGESTION.length, sets: [{ key: 'saveState', value: 'saved' }] },
        ],
      },
    },
    { at: 8700, action: { kind: 'beat', text: '4 · Done editing hands it back' } },
    { at: 8700, action: { kind: 'cursor', x: 1011, y: 28, ms: 1200 } },
    { at: 9800, action: { kind: 'set', key: 'bannerLit', value: true } },
    { at: 10200, action: { kind: 'click' } },
    { at: 10700, action: { kind: 'overlay', which: 'exit', show: true } },
    { at: 11050, action: { kind: 'set', key: 'page', value: 'C' } },
    { at: 11050, action: { kind: 'cursor', x: 700, y: 500, ms: 0 } },
    { at: 11400, action: { kind: 'overlay', which: 'exit', show: false } },
    { at: 11400, action: { kind: 'beat', text: '5 · Your edit is saved for the team' } },
    { at: 11800, action: { kind: 'set', key: 'resumeActionLit', value: true } },
    { at: 11800, action: { kind: 'set', key: 'noticeShown', value: true } },
    { at: 12100, action: { kind: 'cursorHide' } },
    { at: 14300, action: { kind: 'beat', text: null } },
    { at: 14300, action: { kind: 'veil', show: true } },
  ],
};

const briefComments: SceneDefinition = {
  id: 'comments',
  label: 'Comments demo',
  initialCursor: { x: 1060, y: 660 },
  initialVars: {
    sweepOn: false,
    pillVisible: false,
    composerOpen: false,
    composerName: '',
    composerText: '',
    posted: false,
    openCount: 0,
  },
  timeline: [
    // Measured against the rendered brief stage (canvas boxes): the selected
    // key message "Recovery is a plan, not a mystery." renders on one line at
    // x 65-302, y 232-254.5; the pill at x 268-359.3, y 184-212.5; the
    // composer's name field x 770-1042, y 120.5-158, its body field x
    // 770-1042, y 166-218 while empty (when it is clicked) and y 166-223 once
    // the two-line comment is in it, and Post comment x 941.5-1042, y
    // 235-263.5 with the comment typed. As on the script scene, the 16px
    // cursor dot is positioned by its top-left corner, so the sweep's y and
    // its end x sit 8px up and left of where the dot's centre must land (on
    // the text line, on the selection's right edge), while its start x sits
    // 6px left of the text start so the dot begins just inside the first
    // glyph; the clicks use the target's box centre in the state it is in
    // when clicked.
    { at: 300, action: { kind: 'beat', text: '1 · Highlight any line' } },
    { at: 600, action: { kind: 'cursor', x: 59, y: 235, ms: 800 } },
    { at: 1500, action: { kind: 'set', key: 'sweepOn', value: true } },
    { at: 1500, action: { kind: 'cursor', x: 294, y: 235, ms: 700 } },
    { at: 2500, action: { kind: 'set', key: 'pillVisible', value: true } },
    { at: 2500, action: { kind: 'cursor', x: 314, y: 198, ms: 500 } },
    { at: 3100, action: { kind: 'click' } },
    { at: 3400, action: { kind: 'beat', text: '2 · Leave your note right there' } },
    { at: 3400, action: { kind: 'set', key: 'composerOpen', value: true } },
    { at: 3400, action: { kind: 'cursor', x: 906, y: 139, ms: 800 } },
    { at: 4300, action: { kind: 'click' } },
    { at: 4500, action: { kind: 'type', key: 'composerName', text: BRIEF_TYPED_COMMENT_NAME, charMs: 90 } },
    { at: 5100, action: { kind: 'cursor', x: 906, y: 192, ms: 600 } },
    { at: 5800, action: { kind: 'click' } },
    { at: 6100, action: { kind: 'type', key: 'composerText', text: BRIEF_TYPED_COMMENT_BODY, charMs: 32 } },
    { at: 8100, action: { kind: 'cursor', x: 992, y: 249, ms: 700 } },
    { at: 8900, action: { kind: 'click' } },
    { at: 9200, action: { kind: 'set', key: 'composerOpen', value: false } },
    { at: 9200, action: { kind: 'set', key: 'posted', value: true } },
    { at: 9200, action: { kind: 'set', key: 'openCount', value: 1 } },
    { at: 9200, action: { kind: 'beat', text: '3 · It lands in the sidebar' } },
    { at: 11200, action: { kind: 'cursorHide' } },
    { at: 13000, action: { kind: 'beat', text: null } },
    { at: 13000, action: { kind: 'veil', show: true } },
  ],
};

const briefGeneralNotes: SceneDefinition = {
  id: 'general-notes',
  label: 'General notes demo',
  initialCursor: { x: 1060, y: 660 },
  initialVars: {
    page: 'A',
    notesActionLit: false,
    saveState: 'idle',
    typed: '',
  },
  timeline: [
    // Measured against the rendered brief stage (canvas boxes): review card
    // action 3 x 713-1039, y 221.5-285 and the notes field x 219-881, y
    // 304-444, both identical to the script stage's (only the document on
    // page A and one word of page B's helper line differ), so this timeline
    // keeps the script's coordinates.
    { at: 300, action: { kind: 'beat', text: '1 · Open general notes' } },
    { at: 500, action: { kind: 'cursor', x: 876, y: 253, ms: 1100 } },
    { at: 1700, action: { kind: 'set', key: 'notesActionLit', value: true } },
    { at: 2100, action: { kind: 'click' } },
    { at: 2600, action: { kind: 'set', key: 'page', value: 'B' } },
    { at: 2600, action: { kind: 'cursor', x: 700, y: 300, ms: 0 } },
    { at: 2900, action: { kind: 'beat', text: '2 · Big-picture thoughts go here' } },
    { at: 3300, action: { kind: 'cursor', x: 550, y: 374, ms: 600 } },
    { at: 3950, action: { kind: 'click' } },
    {
      at: 4200,
      action: {
        kind: 'type',
        key: 'typed',
        text: BRIEF_TYPED_NOTE,
        // Same pace as the script's; the note is kept short enough to finish
        // before the "It autosaves" beat at 7600 (78 chars, ends 7164 nominal;
        // the chained per-char timers land about 150ms later in practice).
        charMs: 38,
        // Same milestone pattern as the script variant (see the comment there).
        milestones: [
          { charCount: 10, sets: [{ key: 'saveState', value: 'saving' }] },
          { charCount: BRIEF_TYPED_NOTE.length, sets: [{ key: 'saveState', value: 'saved' }] },
        ],
      },
    },
    { at: 7600, action: { kind: 'beat', text: '3 · It autosaves' } },
    { at: 9600, action: { kind: 'cursorHide' } },
    { at: 11000, action: { kind: 'beat', text: null } },
    { at: 11000, action: { kind: 'veil', show: true } },
  ],
};

export const TOUR_SCENES: Record<ReviewKind, Record<SceneId, SceneDefinition>> = {
  script: {
    'suggest-edits': suggestEdits,
    comments,
    'general-notes': generalNotes,
  },
  brief: {
    'suggest-edits': briefSuggestEdits,
    comments: briefComments,
    'general-notes': briefGeneralNotes,
  },
};
